// «Esto ya lo has cursado». El freno al duplicado, convertido en pregunta.
//
// POR QUÉ NO ES UN MURO
// ---------------------
// El backend detecta el duplicado con una huella GRUESA —empresa, cliente, nº
// de líneas y total de unidades—, porque la solicitud guardada no almacena
// todas las referencias: no caben en los 4000 caracteres de la auditoría. Así
// que dos pedidos distintos del mismo cliente que casualmente sumen igual se
// parecen entre sí para el detector.
//
// Y hay estados que bloquean sin que nada haya ido bien: una solicitud se queda
// en `enviada` cuando el flujo contesta sin número de pedido, y también cuando
// no hay URL de creación configurada y la llamada va en MOCK. Con un 409 a
// secas, eso dejaba al usuario sin poder reenviar durante horas y sin nadie a
// quien pedir que lo desbloqueara.
//
// La pregunta tiene que CONTAR de qué va, no sólo avisar: cuándo fue, en qué
// estado está y —lo que de verdad decide— si de aquella solicitud ya salió un
// pedido. Un «¿seguro?» sin datos se contesta que sí siempre.
import { useTranslation } from 'react-i18next'

import { cromado } from './cromado'

export interface Duplicada {
  solicitudId?: string
  estado?: string
  pedido?: string
  cuando?: string
  lineas?: number
  unidades?: number
}

/** Minutos desde la solicitud anterior, o `null` si no vino la fecha. */
export function minutosDesde(iso: string | undefined, ahora = Date.now()): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return Math.max(0, Math.round((ahora - t) / 60000))
}

interface Props {
  dark: boolean
  datos: Duplicada
  enviando: boolean
  onCancelar: () => void
  onEnviar: () => void
}

export function AvisoDuplicada({ dark, datos, enviando, onCancelar, onEnviar }: Props) {
  const { t } = useTranslation()
  const c = cromado(dark)

  const minutos = minutosDesde(datos.cuando)
  const cuando = minutos == null
    ? t('salesOrders.desdeArchivo.duplicada.haceUnRato')
    : t('salesOrders.desdeArchivo.duplicada.hace', { count: minutos })

  // El pedido ya creado es el único caso en el que repetir tiene consecuencia
  // segura, así que se dice aparte y en rojo.
  const yaCreado = datos.estado === 'creada' && !!datos.pedido

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center p-6">
      <div
        onClick={onCancelar}
        className="absolute inset-0 cursor-pointer"
        style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
          dark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
        }`}
      >
        <div className="px-5 pt-5 pb-4 space-y-3">
          <div className="flex items-start gap-3">
            <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${
              dark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-100 text-amber-600'
            }`}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
                <path d="M12 9v4M12 17h.01" />
              </svg>
            </span>
            <div className="min-w-0">
              <h3 className={`text-[14px] font-semibold ${c.mainTxt}`}>
                {t('salesOrders.desdeArchivo.duplicada.titulo')}
              </h3>
              <p className={`text-[12px] leading-relaxed mt-1 ${c.mutedTxt}`}>
                {t('salesOrders.desdeArchivo.duplicada.intro', {
                  cuando,
                  lineas: datos.lineas ?? 0,
                  unidades: (datos.unidades ?? 0).toLocaleString(),
                })}
              </p>
            </div>
          </div>

          <div className={`px-3.5 py-2.5 rounded-lg border text-[12px] leading-relaxed ${
            yaCreado
              ? (dark ? 'bg-red-500/[0.07] border-red-500/20 text-red-300'
                      : 'bg-red-50 border-red-200 text-red-700')
              : (dark ? 'bg-white/[0.03] border-white/10 ' + c.mutedTxt
                      : 'bg-slate-50 border-slate-200 ' + c.mutedTxt)
          }`}>
            {yaCreado
              ? t('salesOrders.desdeArchivo.duplicada.yaCreado', { pedido: datos.pedido })
              : t(`salesOrders.desdeArchivo.duplicada.estados.${datos.estado ?? 'pendiente'}`, {
                  defaultValue: t('salesOrders.desdeArchivo.duplicada.estados.pendiente'),
                })}
          </div>

          <p className={`text-[11px] leading-relaxed ${c.dimTxt}`}>
            {t('salesOrders.desdeArchivo.duplicada.consecuencia')}
          </p>
        </div>

        <div className={`flex items-center justify-end gap-2 px-5 py-3 border-t ${
          dark ? 'border-white/8 bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
        }`}>
          <button
            type="button"
            onClick={onCancelar}
            className={`px-3 py-2 rounded-lg text-[12px] font-medium transition-colors cursor-pointer ${
              dark ? 'text-slate-300 hover:bg-white/6' : 'text-slate-600 hover:bg-slate-200'
            }`}
          >
            {t('salesOrders.desdeArchivo.duplicada.cancelar')}
          </button>
          <button
            type="button"
            onClick={onEnviar}
            disabled={enviando}
            className={`px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors shadow-sm ${
              enviando
                ? `cursor-not-allowed ${dark ? 'bg-white/8 text-slate-500' : 'bg-slate-200 text-slate-400'}`
                : 'bg-amber-600 text-white hover:bg-amber-500 cursor-pointer'
            }`}
          >
            {t(enviando
              ? 'salesOrders.desdeArchivo.cursando'
              : 'salesOrders.desdeArchivo.duplicada.enviar')}
          </button>
        </div>
      </div>
    </div>
  )
}
